import Image from 'next/image';
import {
  TypeImage,
  TypeImageFields,
  TypeMuxVideoFields,
  TypeVideoFields,
} from '@/core/generated-types';
import { Entry } from 'contentful';
import { Video } from '@/components/ui';
import classNames from 'classnames';
import { Dispatch, RefObject, SetStateAction } from 'react';
import { MuxVideo } from '@/components/ui/MuxVideo';

export type TypeMedia = Entry<
  TypeImageFields | TypeVideoFields | TypeMuxVideoFields
>;

export interface GetMediaComponentProps {
  media: TypeMedia;
  classes?: string;
  autoplay?: boolean;
  controls?: boolean;
  muted?: boolean;
  ref?: RefObject<HTMLVideoElement> | RefObject<HTMLImageElement>;
  videoStatus?: {
    isReady?: boolean;
    setIsReady?: Dispatch<SetStateAction<boolean>>;
  };
}

export function getImageSrc(image: TypeImage) {
  return image?.fields?.Image?.fields?.file?.url;
}

export function isMuxVideo(media: TypeMedia) {
  return media?.fields && 'muxVideoUpload' in media?.fields;
}

export function isVideo(media: TypeMedia) {
  return media?.fields && 'video' in media?.fields;
}

export function getMediaComponent({
  media,
  classes = '',
  autoplay = false,
  controls = false,
  muted = true,
  ref,
  videoStatus,
}: GetMediaComponentProps) {
  if (isMuxVideo(media)) {
    const mediaFields = media.fields as TypeMuxVideoFields;
    const playbackId = mediaFields?.muxVideoUpload?.playbackId;
    const poster = mediaFields.poster;

    if (!playbackId) return null;

    const onReady = () => {
      if (videoStatus?.setIsReady) {
        videoStatus.setIsReady(true);
      }
    };

    const props = {
      classes,
      title: media.fields.entryName || '',
      playbackId,
      autoplay,
      poster: mediaFields.poster?.fields?.file?.url,
      controls,
      muted,
      onReady,
    };

    const shouldShowLoadingImage = videoStatus?.isReady === false && !!poster;

    if (classes) {
      // Wrapper is required to fit the MuxPlayer into the parent container
      return (
        <div className="relative w-full h-full">
          {shouldShowLoadingImage && (
            <div
              className="aspect-video bg-slate-300"
              data-testid="loading-placeholder"
            >
              <Image
                className="aspect-video"
                src={`https:${poster.fields.file.url}`}
                width={poster.fields.file.details.image?.width}
                height={poster.fields.file.details.image?.height}
                alt="poster image"
              />
            </div>
          )}
          <div
            className={classNames(
              classes,
              'overflow-hidden',
              shouldShowLoadingImage && 'hidden',
            )}
          >
            <MuxVideo {...props} />
          </div>
        </div>
      );
    }

    return <MuxVideo {...props} />;
  }

  if (isVideo(media)) {
    const mediaFields = media?.fields as TypeVideoFields;
    const videoSrc = mediaFields?.video?.fields?.file?.url;
    const videoPoster = mediaFields?.poster?.fields?.file?.url;

    if (!videoSrc) return null;

    return (
      <Video
        classes={classNames(`${classes} object-cover`)}
        poster={videoPoster}
        src={videoSrc}
        autoplay={autoplay}
        ref={ref as RefObject<HTMLVideoElement>}
        controls={controls}
        muted={muted}
      />
    );
  }

  const imageMedia = media as TypeImage;
  const imageSrc = getImageSrc(imageMedia);
  // Note: Next's Image requires an explicit protocol. In the event one isn't present, we must prepend it.
  const safeImageSrc = imageSrc.includes('http')
    ? imageSrc
    : `https:${imageSrc}`;

  return (
    <Image
      alt={imageMedia.fields.altText}
      aria-hidden
      className={classNames(`${classes} object-cover`)}
      fill
      priority
      src={safeImageSrc}
      ref={ref as RefObject<HTMLImageElement>}
    />
  );
}
